import Data from 'data';
import { Quality } from "common";
import { baseNoDurability, gemStats, itemCheckMod, itemDefenseRange, itemGetModGroups, itemGetType, itemGetTypes, itemMaxSockets, modsToStats, randomRareName, runewordStats, uniqueStats } from "./item";
import { itemCreateDescription } from './itemDescription';

/*
item structure
{
  base: base id, required
  quality: Quality.NORMAL / Quality.RARE etc, required
  ilvl: item level, defaults to 99
  unidentified: bool
  unique: unique/set/runeword id (string)
  name: rare/crafted name
  ethereal: bool
  sockets: number
  mods: {}
  auto: {}
  staff: {}
  crafted: {}
  superior: {}
  corrupted: {}
  uniqueValues: []
  defense: base defense
  ethbugged: boolean
  socketedItems: []
  template: boolean
  stats: {}
}
*/

function defaultModValues(mod) {
  const values = [];
  if (mod.mod1code) values[0] = mod.mod1max;
  if (mod.mod2code) values[1] = mod.mod2max;
  if (mod.mod3code) values[2] = mod.mod3max;
  if (mod.mod4code) values[3] = mod.mod4max;
  if (mod.mod5code) values[4] = mod.mod5max;
  return values;
}

// this will MUTATE the item itself
export function itemUpdate(item) {
  let stats = {};
  if (!item.custom) {
    item.stats = stats;
  }
  const base = Data.items[item.base];
  if (item.unidentified || !base) return;

  const types = itemGetTypes(item.base);

  let fixedStats = null;
  if (item.unique) {
    const varStats = {};
    if (item.quality === Quality.UNIQUE) {
      uniqueStats(stats, Data.uniqueItems[item.unique], item.uniqueValues, item);
      uniqueStats(varStats, Data.uniqueItems[item.unique], null, item);
    } else if (item.quality === Quality.SET) {
      uniqueStats(stats, Data.setItems[item.unique], item.uniqueValues, item);
      uniqueStats(varStats, Data.setItems[item.unique], null, item);
    } else if (item.quality === Quality.RUNEWORD) {
      runewordStats(stats, Data.runes[item.unique], item.uniqueValues, item);
      runewordStats(varStats, Data.runes[item.unique], null, item);
    }
    fixedStats = {};
    for (const id in stats) {
      if (stats[id] === varStats[id] && !isNaN(stats[id])) {
        fixedStats[id] = stats[id];
      }
    }
  }

  if (item.custom) {
    stats = item.stats;
  } else {
    const mods = {
      ...item.mods,
      ...item.auto,
      ...item.crafted,
      ...item.superior,
      ...item.corrupted,
      ...item.staff,
    };
    modsToStats(stats, mods, item);
  }

  if ((baseNoDurability(base, item.quality) || (stats.item_indesctructible && item.quality !== Quality.RUNEWORD)) && !stats.item_ethereal) {
    item.ethereal = false;
  }

  const rawStats = {...stats};

  if (types.has('blun')) {
    if (item.custom) {
      rawStats.item_undeaddamage_percent = (rawStats.item_undeaddamage_percent || 0) - 50;
      if (!rawStats.item_undeaddamage_percent) delete rawStats.item_undeaddamage_percent;
    } else {
      stats.item_undeaddamage_percent = (stats.item_undeaddamage_percent || 0) + 50;
    }
  }

  // update other stuff based on stats

  if (!base.hasinv) {
    item.sockets = 0;
  } else if (stats.item_numsockets) {
    item.sockets = stats.item_numsockets;
  } else {
    item.sockets = Math.min(item.sockets || 0, itemMaxSockets(base, item.quality));
  }
  if (item.socketedItems && item.socketedItems.length > (item.sockets || 0)) {
    item.socketedItems = item.socketedItems.slice(0, item.sockets || 0);
  } else if (!item.socketedItems && item.sockets) {
    item.socketedItems = [];
  }

  if (stats.ethereal) {
    item.ethereal = true;
    item.ethbugged = false;
  }
  if (item.superior && Object.keys(item.superior).length) {
    item.ethbugged = false;
  }

  const defRange = itemDefenseRange(item);
  if (item.defense && defRange) {
    item.defense = Math.max(Math.min(item.defense, defRange.maxac), defRange.minac);
  } else if (defRange) {
    item.defense = defRange.maxac;
  }

  if (!item.template) {
    item.description = itemCreateDescription(item, rawStats, fixedStats);
  }

  return item;
}

export function itemSetQuality(item, quality) {
  if (item.unique || item.quality === quality) return item; // can't change unique item quality
  item = {...item, quality};
  const types = itemGetTypes(item.base);
  if (quality === Quality.RARE || quality === Quality.CRAFTED) {
    if (!item.name) item.name = randomRareName(types);
  }
  if (quality === Quality.CRAFTED) {
    item.ethereal = false;
  }
  if (quality !== Quality.NORMAL) {
    item.ethbugged = false;
  }
  if (quality !== Quality.MAGIC && quality !== Quality.RARE && quality !== Quality.CRAFTED) {
    delete item.mods;
  } else if (!item.mods) {
    item.mods = {};
  } else {
    const maxPerType = (quality === Quality.MAGIC ? 1 : 3);
    const maxTotal = (quality === Quality.CRAFTED ? 4 : maxPerType * 2);
    let prefix = 0, suffix = 0;
    const mods = {};
    for (const id in item.mods) {
      const mod = Data.mods[id];
      if (!mod) continue;
      if (!mod.rare && quality !== Quality.MAGIC) continue;
      if (Data.magicPrefix[id]) {
        if (prefix < maxPerType && prefix + suffix < maxTotal) {
          prefix += 1;
          mods[id] = item.mods[id];
        }
      } else if (Data.magicSuffix[id]) {
        if (suffix < maxPerType && prefix + suffix < maxTotal) {
          suffix += 1;
          mods[id] = item.mods[id];
        }
      }
    }
    item.mods = mods;
  }

  const base = Data.items[item.base];

  if (quality !== Quality.SUPERIOR) {
    delete item.superior;
  } else if (!item.superior) {
    item.superior = {};
    const type = itemGetType(types);
    const id = Object.keys(Data.qualityItems).find(id => {
      const mod = Data.qualityItems[id];
      if (!mod[type]) return false;
      return itemCheckMod(mod, base, types, quality, 100);
    });
    if (id) item.superior[id] = defaultModValues(Data.qualityItems[id]);
  }
  if (quality !== Quality.CRAFTED) {
    delete item.crafted;
  } else if (!item.crafted) {
    item.crafted = {};
    const id = Object.keys(Data.crafted).find(id => itemCheckMod(Data.crafted[id], base, types, quality, 100));
    if (id) item.crafted[id] = defaultModValues(Data.crafted[id]);
  }

  if (!Data.info.pd2 || (quality !== Quality.MAGIC && quality !== Quality.RARE && quality !== Quality.UNIQUE && quality !== Quality.SET && quality !== Quality.CRAFTED)) {
    delete item.corrupted;
  } else if (!item.corrupted) {
    item.corrupted = {};
  }

  itemUpdate(item);

  return item;
}

export function itemSetBase(item, base) {
  if (!Data.items[base] || item.base === base) return item; // can't change unique item quality
  item = {...item, base};
  delete item.defense;
  itemUpdate(item);
  return item;
}

export function itemCreateTemplate(id, type) {
  if (Data.items[id]) {
    const item = {base: id, quality: Data.itemTypes[Data.items[id].type]?.magic ? Quality.MAGIC : Quality.NORMAL, template: true};
    if (item.quality === Quality.MAGIC) {
      item.mods = {};
    }
    return itemUpdate(item);
  }
  if (Data.uniqueItems[id] || Data.setItems[id]) {
    const uniq = Data.uniqueItems[id] || Data.setItems[id];
    const uniqueValues = [];
    for (let i = 1; i <= 12; ++i) {
      if (uniq[`prop${i}`]) {
        const min = uniq[`min${i}`];
        const max = uniq[`max${i}`];
        if (max && min < max) {
          uniqueValues[i - 1] = {min, max};
        } else {
          uniqueValues[i - 1] = max;
        }
      }
    }
    return itemUpdate({base: uniq.code || uniq.item, quality: Data.uniqueItems[id] ? Quality.UNIQUE : Quality.SET, unique: id, uniqueValues, template: true});
  }
  if (Data.runes[id]) {
    const uniq = Data.runes[id];
    const uniqueValues = [];
    for (let i = 1; i <= 7; ++i) {
      if (uniq[`t1code${i}`]) {
        const min = uniq[`t1min${i}`];
        const max = uniq[`t1max${i}`];
        if (max && min < max) {
          uniqueValues[i - 1] = {min, max};
        } else {
          uniqueValues[i - 1] = max;
        }
      }
    }
    const stats = runewordStats({}, uniq, uniqueValues);
    const types = new Set([type]);
    for (let i = 1; i <= 6; ++i) {
      if (uniq[`rune${i}`]) gemStats(stats, uniq[`rune${i}`], types);
    }
    return {quality: Quality.RUNEWORD, unique: id, stats, template: true};
  }
}

const validateValues = values => Array.isArray(values) ? values.map(value => (typeof value !== 'number' || isNaN(value)) ? 0 : value) : [];
function validateMods(mods, ...list) {
  if (!mods || typeof mods !== "object") return {};
  const result = {};
  for (const id in mods) {
    if (list.some(obj => obj[id])) {
      result[id] = validateValues(mods[id]);
    }
  }
  return result;
}

export function itemFix(item) {
  if (!item || !Data.items[item.base]) return null;
  if (item.unidentified) return null;
  if (item.quality === Quality.UNIQUE && !Data.uniqueItems[item.unique]) return null;
  if (item.quality === Quality.SET && !Data.setItems[item.unique]) return null;
  if (item.quality === Quality.RUNEWORD && !Data.runes[item.unique]) return null;
  item = {...item};
  if (!item.ilvl) item.ilvl = 99;

  const base = Data.items[item.base];
  const type = Data.itemTypes[base.type];
  if (!type) return null;

  if (item.custom) {
    if (!item.stats) item.stats = {};
    for (const id in item.stats) {
      if (isNaN(item.stats[id])) {
        delete item.stats[id];
      }
    }
    if (item.stats.poisonlength) {
      item.stats.poison_count = 1;
    }

    if (item.mods) delete item.mods;
    if (item.crafted) delete item.crafted;
    if (item.auto) delete item.auto;
    if (item.staff) delete item.staff;
    if (item.superior) delete item.superior;
    if (item.uniqueValues) delete item.uniqueValues;
    if (item.corrupted) delete item.corrupted;
  } else {
    if (item.quality === Quality.MAGIC || item.quality === Quality.RARE || item.quality === Quality.CRAFTED) {
      item.mods = validateMods(item.mods, Data.magicPrefix, Data.magicSuffix);
    } else {
      if (item.mods) delete item.mods;
    }
    if (item.quality === Quality.CRAFTED) {
      item.crafted = validateMods(item.crafted, Data.crafted);
    } else {
      if (item.crafted) delete item.crafted;
    }
    if (item.quality === Quality.UNIQUE || item.quality === Quality.SET) {
      if (item.auto) delete item.auto;
      if (item.staff) delete item.staff;
    } else {
      if (base.autoprefix) {
        item.auto = validateMods(item.auto, Data.autoMagic);
      } else {
        if (item.auto) delete item.auto;
      }
      if (type.staffmods) {
        item.staff = validateMods(item.staff, Data.staffMods);
      } else {
        if (item.staff) delete item.staff;
      }
    }
    if (item.quality === Quality.SUPERIOR || item.quality === Quality.RUNEWORD) {
      item.superior = validateMods(item.superior, Data.qualityItems);
    } else {
      if (item.superior) delete item.superior;
    }
    if (item.quality === Quality.UNIQUE || item.quality === Quality.SET || item.quality === Quality.RUNEWORD) {
      item.uniqueValues = validateValues(item.uniqueValues);
    } else {
      if (item.unique) delete item.unique;
      if (item.uniqueValues) delete item.uniqueValues;
    }
    if (!Data.info.pd2 || (item.quality !== Quality.MAGIC && item.quality !== Quality.RARE && item.quality !== Quality.UNIQUE && item.quality !== Quality.SET && item.quality !== Quality.CRAFTED)) {
      if (item.corrupted) delete item.corrupted;
    } else if (!item.corrupted) {
      item.corrupted = validateMods(item.corrupted, Data.corruptedMods);
    }
  
    if ((item.auto && !Object.keys(item.auto).length) || (item.quality === Quality.SUPERIOR && item.superior && !Object.keys(item.superior).length)) {
      function _add(to, mods) {
        if (!to || Object.keys(to).length) return;
        if (!mods || !mods.length) return;
        const id = mods[0].mods[mods[0].mods.length - 1];
        const mod = Data.mods[id];
        const values = [];
        if (mod.mod1max != null) values[0] = mod.mod1max;
        if (mod.mod2max != null) values[1] = mod.mod2max;
        if (mod.mod3max != null) values[2] = mod.mod3max;
        if (mod.mod4max != null) values[3] = mod.mod4max;
        to[id] = values;
      }
      const mods = itemGetModGroups(item.base, item.quality);
      _add(item.auto, mods.auto);
      if (item.quality === Quality.SUPERIOR) {
        _add(item.superior, mods.superior);
      }
    }
  }
  
  if (item.quality === Quality.RARE || item.quality === Quality.CRAFTED) {
    if (!item.name || typeof item.name !== "string") item.name = randomRareName(itemGetTypes(item.base));
    else if (item.name.length > 32) item.name = item.name.substring(0, 32);
  }

  return itemUpdate(item);
}

export function itemCreateNormal(id, quality) {
  const base = Data.items[id];
  if (!base) return null;
  const item = {
    base: id,
    quality: quality == null ? (Data.itemTypes[base.type]?.magic ? Quality.MAGIC : Quality.NORMAL) : quality,
    ilvl: 99,
  };
  return itemFix(item);
}

export function itemCreateUnique(id, profile) {
  const uniq = Data.uniqueItems[id] || Data.setItems[id];
  if (!uniq) return null;
  const uniqueValues = [];
  for (let i = 1; i <= 12; ++i) {
    if (uniq[`prop${i}`]) {
      if (uniq[`prop${i}`] === 'randclassskill' && profile) {
        uniqueValues[i - 1] = Data.classes.indexOf(profile.class);
      } else {
        uniqueValues[i - 1] = uniq[`max${i}`];
      }
    }
  }
  return itemFix({
    base: uniq.code || uniq.item,
    quality: Data.uniqueItems[id] ? Quality.UNIQUE : Quality.SET,
    ilvl: 99,
    unique: id,
    uniqueValues,
  });
}

export function itemCreateRuneword(id, base, source) {
  const uniq = Data.runes[id];
  if (!uniq || !Data.items[base]) return null;
  const uniqueValues = [];
  for (let i = 1; i <= 7; ++i) {
    if (uniq[`t1code${i}`]) {
      uniqueValues[i - 1] = uniq[`t1max${i}`];
    }
  }
  const runes = [uniq.rune1, uniq.rune2, uniq.rune3, uniq.rune4, uniq.rune5, uniq.rune6].filter(Boolean);
  return itemFix({
    base: base,
    quality: Quality.RUNEWORD,
    ilvl: source?.ilvl || 99,
    unique: id,
    uniqueValues,
    superior: source?.superior || {},
    defense: source?.defense,
    ethereal: source?.ethereal,
    ethbugged: source?.ethbugged,
    sockets: runes.length,
    socketedItems: runes,
  });
}

const sameValue = (a, b) => (a == null && b == null) || a === b;
const sameArray = (a, b) => (a == null && b == null) || (a && b && a.length === b.length && a.every((v, i) => (v || 0) === (b[i] || 0)));
const sameObject = (a, b) => (a == null && b == null) || (a && b && [...new Set([...Object.keys(a), ...Object.keys(b)])].every(key => sameValue(a[key], b[key])));
const sameObjectA = (a, b) => (a == null && b == null) || (a && b && [...new Set([...Object.keys(a), ...Object.keys(b)])].every(key => sameArray(a[key], b[key])));

export function itemsEqual(item1, item2) {
  if (item1.base !== item2.base) return false;
  if (item1.quality !== item2.quality) return false;
  if (!sameValue(item1.ilvl, item2.ilvl)) return false;
  if (!sameValue(item1.unique, item2.unique)) return false;
  if (!sameValue(item1.name, item2.name)) return false;
  if (!item1.unidentified !== !item2.unidentified) return false;
  if (!item1.ethereal !== !item2.ethereal) return false;
  if (!item1.ethbugged !== !item2.ethbugged) return false;
  if (!item1.custom !== !item2.custom) return false;
  if ((item1.sockets || 0) !== (item2.sockets || 0)) return false;
  if ((item1.defense || 0) !== (item2.defense || 0)) return false;

  if (item1.custom) {
    if (!sameObject(item1.stats, item2.stats)) return false;
  } else {
    if (!sameObjectA(item1.mods, item2.mods)) return false;
    if (!sameObjectA(item1.auto, item2.auto)) return false;
    if (!sameObjectA(item1.staff, item2.staff)) return false;
    if (!sameObjectA(item1.crafted, item2.crafted)) return false;
    if (!sameObjectA(item1.superior, item2.superior)) return false;
    if (!sameObjectA(item1.corrupted, item2.corrupted)) return false;
    if (!sameArray(item1.uniqueValues, item2.uniqueValues)) return false;
  }

  if (!sameArray(item1.socketedItems, item2.socketedItems)) return false;

  return true;
}
